import React, { FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'

import { noop } from 'lodash-es'
import { useTheme } from 'styled-components/native'

import { AuroraCareOption, AuroraCareOptionsMetadata } from '../../careOptionsMetadata/auroraCareOptionsMetadata'
import { useGetResponsiveAssets } from '../../hooks/useGetResponsiveAssets'
import { AuroraThemeType } from '../../utils/themes/ThemeProvider'
import { SplitHeroCard } from '../heroCard/SplitHeroCard'

export interface AuroraHeroCardProps {
  careOption: AuroraCareOption
  formattedMessageValues?: Record<string, any>
  onCTAPress?: () => void
  buttonTextOverride?: React.ReactNode
  ctaSection?: React.ReactNode
  isFullWidth?: boolean
  /**
   * When true, the card fills its container width instead of SplitHeroCard's default 312/870 maxWidth cap.
   * Used for carousel cards, whose width is governed by the carousel slot (focusedItemWidth) rather than the
   * card's own cap. This lets a single care option span the full stack footprint, and is a no-op for the
   * multi-option case where the slot already bounds the card to the focused-card width.
   */
  fillContainerWidth?: boolean
}

export const AuroraHeroCard: FunctionComponent<AuroraHeroCardProps> = ({
  careOption,
  formattedMessageValues,
  onCTAPress = noop,
  buttonTextOverride,
  ctaSection,
  isFullWidth = false,
  fillContainerWidth = false,
}) => {
  const cardMetadata = AuroraCareOptionsMetadata[careOption]
  const { id, title, description, imageKey, buttonText } = cardMetadata
  const {
    assets,
    colors,
    spacing,
    breakpoints: { isMinWidthLaptop },
  } = useTheme() as AuroraThemeType
  const testId = `AuroraHeroCard-${id}`
  const imageKeyName = `${imageKey}Hero`

  // Always call the hook (Rules of Hooks), but provide fallback for missing assets
  const imageUrl = useGetResponsiveAssets(assets[imageKeyName]?.assetNames ?? { defaultAsset: '' })

  return (
    <SplitHeroCard
      title={<FormattedMessage {...title} values={formattedMessageValues} />}
      description={<FormattedMessage {...description} values={formattedMessageValues} />}
      imageSource={imageUrl}
      buttonText={buttonTextOverride ?? (buttonText ? <FormattedMessage {...buttonText} /> : undefined)}
      onPress={onCTAPress}
      testID={testId}
      ctaSection={ctaSection}
      customStyles={{
        ...(isFullWidth && { cardContainerStyle: { default: { maxWidth: '100%' }, mobile: { maxWidth: '100%' } } }),
        contentContainerStyle: {
          default: { backgroundColor: colors.components.careOptionCard.container.background },
          ...(isFullWidth && { mobile: { padding: spacing['24px'], paddingBottom: spacing['32px'], height: 'auto' } }),
        },
        imageContainerStyle: {
          default: isFullWidth ? { width: '49%', maxWidth: '49%' } : { width: isMinWidthLaptop ? 389 : 344 },
          mobile: isFullWidth
            ? { width: '100%', maxWidth: '100%' }
            : fillContainerWidth
            ? { width: '100%', maxWidth: '100%' }
            : { width: 312 },
        },
        ...(fillContainerWidth && {
          cardContainerStyle: {
            default: { maxWidth: '100%' },
            mobile: { maxWidth: '100%' },
          },
        }),
      }}
    />
  )
}
